'use client'
import { useCallback, useState, type CSSProperties } from 'react'

/** Keep the photograph intact; its decorative backdrop fills space on every side. */
export function ActivityCover({
    src,
    background,
    className = '',
    priority = false,
}: {
    priority?: boolean
    src?: string | null
    background?: string
    className?: string
}) {
    const [failedSource, setFailedSource] = useState<string | null>(null)
    const [loadedSource, setLoadedSource] = useState<string | null>(null)
    const visibleSource = src && src !== failedSource ? src : null
    const imageRef = useCallback((image: HTMLImageElement | null) => {
        // An SSR image can load or fail before hydration attaches its handlers.
        if (!image?.complete || !visibleSource) return
        if (image.naturalWidth > 0) {
            setLoadedSource(visibleSource)
        } else {
            setFailedSource(visibleSource)
        }
    }, [visibleSource])
    return (
        <div
            data-activity-cover
            className={className}
            style={{
                position: 'relative',
                width: '100%',
                minWidth: 0,
                height: '100%',
                aspectRatio: '16 / 10',
                overflow: 'hidden',
                isolation: 'isolate',
                // Neutral placeholder behind the photo; the theme token keeps it dark in dark mode.
                background: visibleSource ? undefined : background,
                backgroundColor: visibleSource ? 'var(--bg-tertiary, #f3f4f6)' : undefined,
            }}
        >
            {visibleSource && loadedSource === visibleSource && (
                <div
                    aria-hidden="true"
                    data-cover-backdrop
                    style={
                        {
                            position: 'absolute',
                            inset: -24,
                            backgroundImage: `url(${JSON.stringify(visibleSource)})`,
                            backgroundSize: '100% 100%',
                            backgroundPosition: 'center',
                            filter: 'blur(24px)',
                            opacity: 0.75,
                        } as CSSProperties
                    }
                />
            )}
            <div
                data-cover-image
                style={{
                    position: 'absolute',
                    inset: 0,
                    width: '100%',
                    height: '100%',
                }}
            >
                {visibleSource && (
                    <img
                        ref={imageRef}
                        src={visibleSource}
                        alt=""
                        loading={priority ? 'eager' : 'lazy'}
                        fetchPriority={priority ? 'high' : 'auto'}
                        decoding="async"
                        onLoad={() => setLoadedSource(visibleSource)}
                        onError={() => setFailedSource(visibleSource)}
                        style={{ display: 'block', width: '100%', height: '100%', objectFit: 'contain' }}
                    />
                )}
            </div>
        </div>
    )
}
